import { Icon, IconName } from "@/components/common/Icon";
import { RequireOnlyOne } from "@/interfaces/common";
import clsx from "clsx";
import { ButtonHTMLAttributes, ComponentProps } from "react";
import { Link } from "react-router-dom";
import { LoadingSpinner } from "../LoadingSpinner";

type BaseProps = ButtonHTMLAttributes<HTMLButtonElement> & ComponentProps<typeof Link>;

type Props = Readonly<
  BaseProps & {
    icon: IconName;
    iconClassName?: string;
    unshaded?: boolean;
    full?: boolean;
    isLoading?: boolean;
  }
>;

export const IconButton = ({
  icon,
  className,
  to,
  iconClassName,
  unshaded,
  children,
  full,
  isLoading,
  ...rest
}: RequireOnlyOne<Props, "onClick" | "to">) => {
  const baseStyles = clsx(
    "group flex w-fit items-center gap-2 border border-background-secondary p-1",
    "text-body relative !font-normal",
    { "hover:shadow-sm/25": !unshaded, "w-full": full },
    { "opacity-50 cursor-not-allowed": rest.disabled },
  );

  const iconStyles = "transition-opacity group-hover:opacity-50 duration-300";

  if (to) {
    return (
      <Link {...rest} to={to} className={clsx(baseStyles, className)}>
        <ActionContent icon={icon} isLoading={isLoading} className={clsx(iconStyles, iconClassName)}>
          {children}
        </ActionContent>
      </Link>
    );
  }

  return (
    <button {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)} className={clsx(baseStyles, className)}>
      <ActionContent icon={icon} isLoading={isLoading} className={clsx(iconStyles, iconClassName)}>
        {children}
      </ActionContent>
    </button>
  );
};

const ActionContent = ({
  icon,
  children,
  isLoading,
  className,
}: Pick<Props, "icon" | "children" | "isLoading" | "className">) => (
  <>
    <div
      className={clsx("flex items-center gap-2 transition-opacity", {
        "opacity-0": isLoading,
      })}
    >
      <Icon name={icon} className={clsx("text-body transition-colors", className)} />
      {children}
    </div>

    {isLoading && <LoadingSpinner className="!absolute inset-1/2 -translate-x-1/2 -translate-y-1/2" color="primary" />}
  </>
);
